import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Chip,
  Divider,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  styled,
  useMediaQuery,
} from "@mui/material";
import { ArrowBackIosRounded, AttachFileRounded } from "@mui/icons-material";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import ColumnContainer from "../../../common/ColumnContainer.js";
import { SubtitleContainer, TitleContainer } from "../../style.js";
import RowContainer from "../../../common/RowContainer.js";
import { userDashboardRoute } from "../../../../utils/routes/Routes.js";
import { ChatComponent } from "../../../common/ChatComponent.js";
import "./TicketsStyle.less";
import { selectAuthData } from "../../../../redux/features/auth/authSlice.js";
import { RenderColoredCircleIconWithStatus } from "../../../common/RenderColoredCircleIconWithStatus.js";
import { handleDownloadClick } from "../../../../utils/helpers/downloadFile.js";
import {
  useAddMessageToTicketMutation,
  useGetTicketByTicketIdQuery,
} from "../../../../redux/features/tickets/ticketsApi.js";
import {
  getCurrentTicketId,
  selectSingleTicket,
} from "../../../../redux/features/tickets/ticketsSlice.js";
import moment from "moment";
import { FormattedMessage, useIntl } from "react-intl";
import { FormatDateTime } from "../../../../utils/helpers/FormatDateTime.js";
import { DashboardButton } from "../../../common/DashboardButton.js";
import { translateFilterKeys } from "../../../../utils/helpers/translateFilterKeys.js";
import { selectLanguage } from "../../../../redux/features/language/languageSlice.js";

export const SingleUserTicketPage = () => {
  // INIT
  const { id: ticketId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const fileInput = useRef(null);
  const isDesktop = useMediaQuery("(min-width:960px)");
  const intl = useIntl();

  // REDUX
  const userData = useSelector(selectAuthData);
  const userId = userData?.userData?.id;
  const {
    data,
    isLoading,
    isError,
    refetch: refetchSingleTicket,
  } = useGetTicketByTicketIdQuery({ ticketId: ticketId });

  const currentTicket = useSelector(selectSingleTicket);

  const [addMessage, { isLoading: addMessageIsLoading }] =
    useAddMessageToTicketMutation();
  const role = userData?.userData?.role;
  const currentLanguage = useSelector(selectLanguage);

  // STATE
  const [message, setMessage] = useState("");
  const [showMore, setShowMore] = useState(false);
  const [openChat, setOpenChat] = useState(false);
  const [fileUploaded, setFileUploaded] = useState(null);
  const [fileSizeError, setFileSizeError] = useState(false);

  // EFFECTS

  useEffect(() => {
    dispatch(getCurrentTicketId(ticketId));
  }, [ticketId, dispatch]);

  useEffect(() => {
    refetchSingleTicket(ticketId);
  }, [ticketId, refetchSingleTicket, location.pathname]);

  // FUNCTIONS

  const handleToggleShowDescription = () => {
    setShowMore(!showMore);
  };

  const handleGoBackToTickets = () => {
    navigate(userDashboardRoute.tickets);
  };

  const handleNewMessageChange = (e) => {
    setMessage(e.target.value);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file.size > process.env.REACT_APP_MAX_FILE_SIZE) {
      setFileSizeError(true);
      return;
    }
    if (file) {
      setFileUploaded(file);
      e.target.value = "";
      setFileSizeError(false);
    }
  };

  const handleOpenFileInput = () => {
    fileInput.current.click();
  };

  const handleSendMessage = async () => {
    if (message || fileUploaded) {
      const messageData = {
        role: "user",
        message: message ? message : "",
        file: fileUploaded ? fileUploaded : null,
      };
      const formData = new FormData();
      formData.append("message", messageData.message);
      formData.append("role", messageData.role);
      if (messageData.file) {
        formData.append("file", messageData.file);
      }

      try {
        const result = await addMessage({
          ticketId: ticketId,
          userId: userId,
          messageData: formData,
        }).unwrap();
        if (result) {
          dispatch(getCurrentTicketId(ticketId));
          refetchSingleTicket();
          setMessage("");
          setFileUploaded(null);
        }
      } catch (error) {
        console.error("Failed to send message:", error);
      }
    }
  };

  // UTILS
  const StyledTypographyDescription = styled(Typography)({
    width: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    fontSize: "16px",
    display: "-webkit-box",
    WebkitLineClamp: showMore ? "unset" : 2,
    WebkitBoxOrient: "vertical",
    lineHeight: "1.5rem",
    maxHeight: showMore ? "none" : "fit-content",
    color: "var(--text-primary)",
  });

  return (
    <ColumnContainer className="single-ticket-page" halfGap>
      <RowContainer align="center">
        <TitleContainer>
          <ColumnContainer noGap>
            <RowContainer start align="center">
              <RowContainer minGap start align="center">
                <IconButton>
                  <ArrowBackIosRounded
                    onClick={() => handleGoBackToTickets()}
                  />
                </IconButton>
                <Typography variant="h3" component="h3">
                  Ticket #{ticketId}
                </Typography>
              </RowContainer>
              <RenderColoredCircleIconWithStatus string={data?.status || ""} />
            </RowContainer>
          </ColumnContainer>
        </TitleContainer>
      </RowContainer>

      <ColumnContainer halfGap fullHeight>
        {data ? (
          <ColumnContainer>
            <ColumnContainer noGap className={`resume-ticket-section ${role}`}>
              <Grid
                container
                spacing={2}
                justifyContent="center"
                overflow={"auto"}
                marginTop={"6px"}
              >
                <Grid
                  item
                  xs={12}
                  sm={12}
                  md={12}
                  lg={5}
                  height={"fit-content"}
                >
                  <ColumnContainer minGap>
                    <SubtitleContainer>
                      <Typography variant="p" component="p">
                        {data?.title}
                      </Typography>
                    </SubtitleContainer>
                    <RowContainer align="end" noGap>
                      <StyledTypographyDescription>
                        {data?.description}
                      </StyledTypographyDescription>

                      <Typography
                        variant="p"
                        component="p"
                        fontSize="14px"
                        marginLeft="8px"
                        color={"var(--secondary-color)"}
                        onClick={handleToggleShowDescription}
                      >
                        {showMore ? (
                          <FormattedMessage id="button.hide" />
                        ) : (
                          <FormattedMessage id="button.show" />
                        )}
                      </Typography>
                    </RowContainer>
                  </ColumnContainer>
                </Grid>
                <Grid
                  maxHeight={"fit-content"}
                  item
                  xs={0}
                  sm={0}
                  md={0}
                  lg={1}
                  sx={{
                    display: { xs: "none", lg: "flex" },
                    justifyContent: "center",
                  }}
                  paddingLeft={0}
                >
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{
                      borderColor: "var(--main-color)",
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={12} md={12} lg={5} height="fit-content">
                  <ColumnContainer
                    style={{ gap: "0.3rem" }}
                    className="ticket-info"
                  >
                    <RowContainer start halfGap align="center">
                      <Typography
                        variant="p"
                        component="p"
                        className="title-info"
                      >
                        <FormattedMessage id="ticket.category" />:
                      </Typography>
                      <Chip
                        color={"warning"}
                        label={translateFilterKeys(
                          data?.category?.name,
                          currentLanguage
                        )}
                      />
                    </RowContainer>
                    <RowContainer start halfGap>
                      <Typography
                        variant="p"
                        component="p"
                        className="title-info"
                      >
                        <FormattedMessage id="ticket.created" />
                      </Typography>
                      <Typography variant="p" component="p">
                        <FormatDateTime value={data?.created_at} />
                      </Typography>
                    </RowContainer>
                    <RowContainer start halfGap></RowContainer>
                    <RowContainer start halfGap>
                      <Typography
                        variant="p"
                        component="p"
                        className="title-info"
                      >
                        <FormattedMessage id="ticket.attachment" />:
                      </Typography>
                      <RowContainer start halfGap>
                        {data?.attachment?.file_name ? (
                          <Tooltip
                            title={
                              <FormattedMessage id="tooltip.downloadFile" />
                            }
                          >
                            <Typography
                              onClick={() =>
                                handleDownloadClick(
                                  true,
                                  userId,
                                  data?.attachment?.id
                                )
                              }
                              variant="p"
                              component="p"
                              className="user-attachment"
                            >
                              {data?.attachment?.file_name}
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography
                            variant="p"
                            component="p"
                            className="user-attachment"
                          >
                            -
                          </Typography>
                        )}
                      </RowContainer>
                    </RowContainer>
                    <RowContainer minGap start align="start">
                      <Typography
                        variant="p"
                        component="p"
                        className="title-info"
                        color="var(--text-secondary)"
                      >
                        <FormattedMessage id="ticket.resolutionDate" />:
                      </Typography>
                      <Typography variant="p" component="p" fontSize="14px">
                        {data?.resolution_date ? (
                          moment(data?.resolution_date).format("DD/MM/YYYY")
                        ) : (
                          <FormattedMessage id="ticket.noResolutionDate" />
                        )}
                      </Typography>
                    </RowContainer>
                  </ColumnContainer>
                </Grid>
              </Grid>
            </ColumnContainer>

            <ColumnContainer className="communication-section">
              <Typography variant="h6" component="h6">
                <FormattedMessage id="ticket.communications" />
              </Typography>

              <ColumnContainer
                className="chat-messages"
                start
                style={{
                  height:
                    currentTicket?.messages?.length === 0
                      ? "10vh"
                      : "fit-content",
                  overflowY: "scroll",
                  overflowX: "hidden",
                }}
              >
                {currentTicket?.messages.length === 0 ? (
                  <Typography variant="p" component="p">
                    <FormattedMessage id="ticket.noMessages" />
                  </Typography>
                ) : (
                  currentTicket?.messages.map((msg) => (
                    <RowContainer
                      style={{
                        justifyContent:
                          msg.hasOwnProperty("role") && msg.role === "user"
                            ? "flex-end"
                            : "flex-start",
                      }}
                    >
                      <ChatComponent element={msg} generalData={data} />
                    </RowContainer>
                  ))
                )}
              </ColumnContainer>
              <RowContainer start>
                {!openChat && data?.status !== "closed" ? (
                  <DashboardButton
                    type="filled"
                    onClick={() => setOpenChat(true)}
                  >
                    {data?.messages.length === 0 ? (
                      <FormattedMessage id="button.startChat" />
                    ) : (
                      <FormattedMessage id="button.reply" />
                    )}
                  </DashboardButton>
                ) : null}
              </RowContainer>
            </ColumnContainer>
            {openChat ? (
              <ColumnContainer style={{ gap: "8px" }}>
                <RowContainer
                  align="end"
                  className="chat-text-field"
                  // style={{ position: "fixed" }}
                >
                  <TextField
                    multiline
                    rows={4}
                    size="medium"
                    fullWidth
                    value={message || ""}
                    InputProps={{
                      inputProps: {
                        sx: {
                          "&::placeholder": {
                            color: "var(--text-primary)",
                            opacity: 1,
                          },
                        },
                      },

                      endAdornment: (
                        <ColumnContainer noGap>
                          <RowContainer align="center" halfGap>
                            <Tooltip
                              title={
                                <FormattedMessage id="tooltip.uploadFile" />
                              }
                            >
                              <div>
                                <IconButton
                                  className="attach-btn"
                                  onClick={handleOpenFileInput}
                                >
                                  <AttachFileRounded />
                                </IconButton>
                              </div>
                            </Tooltip>
                            <input
                              accept={".zip, .mod, .bin"}
                              style={{ display: "none" }}
                              type="file"
                              ref={fileInput}
                              id="file-upload"
                              onChange={handleFileChange}
                            />
                            <DashboardButton
                              type="filled"
                              onClick={handleSendMessage}
                              disabled={
                                (!message && !fileUploaded) ||
                                addMessageIsLoading
                              }
                            >
                              <RowContainer align="center">
                                <FormattedMessage id="button.send" />
                                <SendRoundedIcon
                                  style={{
                                    transform: "rotate(-30deg)",
                                    marginBottom: "12px",
                                  }}
                                />
                              </RowContainer>
                            </DashboardButton>
                          </RowContainer>
                        </ColumnContainer>
                      ),
                    }}
                    onChange={handleNewMessageChange}
                    placeholder={intl.formatMessage({
                      id: "ticket.type",
                    })}
                  />
                </RowContainer>

                {fileUploaded ? (
                  <Chip
                    className="file-chip"
                    color="default"
                    label={
                      <RowContainer start minGap align="center">
                        <AttachFileRounded fontSize="small" />
                        {fileUploaded.name}
                      </RowContainer>
                    }
                    onDelete={() => setFileUploaded(null)}
                  />
                ) : fileSizeError ? (
                  <Typography
                    component="p"
                    fontSize={"16px"}
                    color={"var(--error)"}
                    textAlign={"center"}
                  >
                    {<FormattedMessage id="file.size.error" />}
                  </Typography>
                ) : null}
              </ColumnContainer>
            ) : null}
          </ColumnContainer>
        ) : isLoading ? (
          <Typography variant="h6" component="h6">
            Loading...
          </Typography>
        ) : isError ? (
          <Typography variant="h6" component="h6">
            Error
          </Typography>
        ) : null}
      </ColumnContainer>
    </ColumnContainer>
  );
};
